import React, { useState, useRef } from 'react';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { validateFullName, validateEmail, validatePhone, TURKISH_NAME_REGEX, TURKISH_ADDRESS_REGEX } from '../../../utils/turkishValidation';
import { formatTurkishPhone } from '../../../utils/phoneFormatter';
import { CITY_OPTIONS, getDistrictOptions } from '../../../constants/turkeyData';

const EditProfileModal = ({ user, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: user?.name,
    bio: user?.bio,
    experienceLevel: user?.experienceLevel,
    hasEquipment: user?.hasEquipment || '',
    location: user?.location || '',
    email: user?.email,
    phone: user?.phone,
    avatar: user?.avatar,
    // TC number and emergency contact fields
    tcNumber: user?.tcNumber || '',
    birthDate: user?.birthDate || '',
    emergencyContactName: user?.emergencyContactName || '',
    emergencyContactPhone: user?.emergencyContactPhone || '',
    // New shipping address fields
    city: user?.city || '',
    district: user?.district || '',
    addressLine: user?.addressLine || '',
    zipCode: user?.zipCode || '',
    // New social media & equipment fields
    instagramHandle: user?.instagramHandle || '',
    websiteUrl: user?.websiteUrl || '',
    equipmentDetails: user?.equipmentDetails || '',
    weightKg: user?.weightKg || '',
    heightCm: user?.heightCm || ''
  });

  const [errors, setErrors] = useState({});
  const [uploading, setUploading] = useState(false);
  const [avatarPreview, setAvatarPreview] = useState(user?.avatar || '');
  const fileInputRef = useRef(null);

  const experienceLevels = [
    { value: 'Başlangıç', label: 'Başlangıç' },
    { value: 'Orta', label: 'Orta' },
    { value: 'İleri', label: 'İleri' },
    { value: 'Uzman', label: 'Uzman' }
  ];

  const equipmentOptions = [
    { value: 'yes', label: 'Evet, kendi ekipmanım var' },
    { value: 'no', label: 'Hayır, kiralama ihtiyacım var' },
    { value: 'planning', label: 'Almayı planlıyorum' }
  ];

  const locationOptions = CITY_OPTIONS;

  const handleChange = (field, value) => {
    if (field === 'city') {
      setFormData((prev) => ({ ...prev, city: value, district: '' }));
    } else {
      setFormData((prev) => ({ ...prev, [field]: value }));
    }
    if (errors?.[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const handleAvatarClick = () => {
    fileInputRef?.current?.click();
  };

  const handleFileChange = async (event) => {
    const file = event?.target?.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowedTypes?.includes(file?.type)) {
      setErrors((prev) => ({ 
        ...prev, 
        avatar: 'Sadece JPEG, PNG veya WebP formatında resim yükleyebilirsiniz' 
      }));
      return;
    }

    // Validate file size (2MB)
    if (file?.size > 2097152) {
      setErrors((prev) => ({ 
        ...prev, 
        avatar: 'Dosya boyutu 2MB\'dan küçük olmalıdır' 
      }));
      return;
    }

    setUploading(true);
    setErrors((prev) => ({ ...prev, avatar: '' }));

    try {
      // Get current user ID
      const { data: { user: authUser }, error: authError } = await supabase?.auth?.getUser();
      if (authError) throw authError;

      const userId = authUser?.id;
      if (!userId) throw new Error('Kullanıcı oturumu bulunamadı');

      // Delete old avatar if exists
      if (formData?.avatar) {
        const oldPath = formData?.avatar?.split('/profile-avatars/')?.[1];
        if (oldPath) {
          await supabase?.storage?.from('profile-avatars')?.remove([oldPath]);
        }
      }

      // Upload new avatar
      const fileExt = file?.name?.split('.')?.pop();
      const fileName = `${userId}/avatar-${Date.now()}.${fileExt}`;
      
      const { data: uploadData, error: uploadError } = await supabase?.storage?.from('profile-avatars')?.upload(fileName, file, {
          cacheControl: '3600',
          upsert: true
        });

      if (uploadError) throw uploadError;

      // Get public URL for the uploaded file
      const { data: urlData } = supabase?.storage?.from('profile-avatars')?.getPublicUrl(uploadData?.path);

      const avatarUrl = urlData?.publicUrl;

      setFormData((prev) => ({ ...prev, avatar: avatarUrl }));
      setAvatarPreview(avatarUrl);

    } catch (err) {
      console.error('Avatar upload error:', err);
      setErrors((prev) => ({ 
        ...prev, 
        avatar: `Yükleme başarısız: ${err?.message || 'Bilinmeyen hata'}` 
      }));
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    const newErrors = {};

    const nameError = validateFullName(formData?.name);
    if (nameError) newErrors.name = nameError;

    const emailError = validateEmail(formData?.email);
    if (emailError) newErrors.email = emailError;

    const phoneError = validatePhone(formData?.phone, false);
    if (phoneError) newErrors.phone = phoneError;

    if (formData?.emergencyContactName?.trim() && !TURKISH_NAME_REGEX?.test(formData?.emergencyContactName?.trim())) {
      newErrors.emergencyContactName = 'Acil durum kişisi adı yalnızca harf ve Türkçe karakter içerebilir';
    }

    if (formData?.city?.trim() && !TURKISH_ADDRESS_REGEX?.test(formData?.city?.trim())) {
      newErrors.city = 'Şehir yalnızca harf ve Türkçe karakter içerebilir';
    }

    if (formData?.district?.trim() && !TURKISH_ADDRESS_REGEX?.test(formData?.district?.trim())) {
      newErrors.district = 'İlçe yalnızca harf ve Türkçe karakter içerebilir';
    }

    if (Object.keys(newErrors)?.length > 0) {
      setErrors(newErrors);
      return;
    }

    onSave(formData);
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="bg-card rounded-xl w-full max-w-4xl my-8"
        onClick={(e) => e?.stopPropagation()}
      >
        <div className="flex items-center justify-between p-6 border-b border-border sticky top-0 bg-card z-10 rounded-t-xl">
          <h2 className="text-2xl font-bold text-foreground">Profili Düzenle</h2>
          <button
            onClick={onClose}
            className="w-10 h-10 flex items-center justify-center rounded-lg hover:bg-muted transition-smooth"
          >
            <Icon name="X" size={22} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-8 max-h-[calc(100vh-180px)] overflow-y-auto">
          {/* Avatar Section */}
          <div className="flex items-center gap-6 pb-6 border-b border-border">
            <div 
              className="relative w-20 h-20 md:w-24 md:h-24 rounded-full bg-muted flex items-center justify-center overflow-hidden cursor-pointer group"
              onClick={handleAvatarClick}
            >
              {avatarPreview ? (
                <>
                  <img 
                    src={avatarPreview} 
                    alt="Profile preview" 
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <Icon name="Camera" size={24} className="text-white" />
                  </div>
                </>
              ) : (
                <Icon name="User" size={32} className="text-muted-foreground" />
              )}
              {uploading && (
                <div className="absolute inset-0 bg-black/70 flex items-center justify-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
                </div>
              )}
            </div>
            <div className="flex-1">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/webp"
                onChange={handleFileChange}
                className="hidden"
                disabled={uploading}
              />
              <Button 
                type="button"
                variant="outline" 
                size="sm" 
                iconName="Upload"
                onClick={handleAvatarClick}
                disabled={uploading}
              >
                {uploading ? 'Yükleniyor...' : 'Fotoğraf Yükle'}
              </Button>
              <p className="text-xs text-muted-foreground mt-2">
                JPEG, PNG veya WebP formatında, maksimum 2MB
              </p>
              {errors?.avatar && (
                <p className="text-xs text-destructive mt-1">{errors?.avatar}</p>
              )}
            </div>
          </div>

          {/* Group 1: Kişisel Bilgiler */}
          <div className="space-y-5">
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
              <span className="text-red-500 font-semibold">*</span>
              <span>ile işaretli alanlar zorunludur</span>
            </div>
            <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
              <Icon name="User" size={20} />
              Kişisel Bilgiler
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Input
                label="Ad Soyad *"
                type="text"
                value={formData?.name}
                onChange={(e) => handleChange('name', e?.target?.value)}
                error={errors?.name}
                required
              />
            </div>

            <Input
              label="Biyografi"
              type="text"
              value={formData?.bio}
              onChange={(e) => handleChange('bio', e?.target?.value)}
              description="Kendinizi kısaca tanıtın"
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Input
                label="E-posta *"
                type="email"
                value={formData?.email}
                onChange={(e) => handleChange('email', e?.target?.value)}
                error={errors?.email}
                required
              />

              <Input
                label="Telefon *"
                type="tel"
                value={formData?.phone}
                onChange={(e) => handleChange('phone', formatTurkishPhone(e?.target?.value))}
                placeholder="0532 123 45 67"
                maxLength={14}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Input
                label="TC Numarası *"
                type="text"
                value={formData?.tcNumber}
                onChange={(e) => handleChange('tcNumber', e?.target?.value)}
                placeholder="11 haneli TC kimlik numaranız"
                maxLength={11}
              />
              
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-foreground">Doğum Tarihi *</label>
                <input
                  type="text"
                  inputMode="numeric"
                  placeholder="GG.AA.YYYY"
                  value={formData?.birthDate
                    ? (() => {
                        const parts = formData?.birthDate?.split('-');
                        if (parts?.length === 3 && parts?.[0]?.length === 4) {
                          return `${parts?.[2]}.${parts?.[1]}.${parts?.[0]}`;
                        }
                        return formData?.birthDate;
                      })()
                    : ''}
                  onChange={(e) => {
                    let val = e?.target?.value?.replace(/[^\d.]/g, '');
                    // Auto-insert dots after day and month
                    const digits = val?.replace(/\./g, '');
                    if (digits?.length <= 2) {
                      val = digits;
                    } else if (digits?.length <= 4) {
                      val = `${digits?.slice(0, 2)}.${digits?.slice(2)}`;
                    } else {
                      val = `${digits?.slice(0, 2)}.${digits?.slice(2, 4)}.${digits?.slice(4, 8)}`;
                    }
                    // Convert to YYYY-MM-DD for storage when fully entered
                    const dotParts = val?.split('.');
                    if (dotParts?.length === 3 && dotParts?.[2]?.length === 4) {
                      const stored = `${dotParts?.[2]}-${dotParts?.[1]?.padStart(2,'0')}-${dotParts?.[0]?.padStart(2,'0')}`;
                      handleChange('birthDate', stored);
                    } else {
                      handleChange('birthDate', val);
                    }
                  }}
                  maxLength={10}
                  className="w-full border border-border rounded-lg px-3 py-2 text-sm text-foreground bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                />
                <p className="text-xs text-muted-foreground">Yaş doğrulaması için gereklidir</p>
              </div>
            </div>
          </div>

          {/* Group 2: SUP Bilgileri */}
          <div className="space-y-5 pt-6 border-t border-border">
            <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
              <Icon name="Waves" size={20} />
              SUP Bilgileri
            </h3>
            <p className="text-sm text-muted-foreground">Ekipman seçimi için gerekli bilgiler</p>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              <Select
                label="Deneyim Seviyesi *"
                options={experienceLevels}
                value={formData?.experienceLevel}
                onChange={(value) => handleChange('experienceLevel', value)}
              />

              <Input
                label="Kilo (kg)"
                type="number"
                value={formData?.weightKg}
                onChange={(e) => handleChange('weightKg', e?.target?.value)}
                placeholder="Örn: 75"
                description="Ekipman seçimi için"
                min="0"
                step="0.1"
              />

              <Input
                label="Boy (cm)"
                type="number"
                value={formData?.heightCm}
                onChange={(e) => handleChange('heightCm', e?.target?.value)}
                placeholder="Örn: 175"
                description="Ekipman seçimi için"
                min="0"
                step="1"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Select
                label="Ekipman Durumu"
                options={equipmentOptions}
                value={formData?.hasEquipment}
                onChange={(value) => handleChange('hasEquipment', value)}
              />

              <Select
                label="Konum"
                options={locationOptions}
                value={formData?.location}
                onChange={(value) => handleChange('location', value)}
              />
            </div>
          </div>

          {/* Group 3: Acil Durum İletişim */}
          <div className="space-y-5 pt-6 border-t border-border">
            <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
              <Icon name="Phone" size={20} />
              Acil Durum İletişim
            </h3>
            <p className="text-sm text-muted-foreground">Etkinliklerde acil durumlarda ulaşılacak kişi bilgileri</p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Input
                label="Acil Durum Kişisi *"
                type="text"
                value={formData?.emergencyContactName}
                onChange={(e) => handleChange('emergencyContactName', e?.target?.value)}
                placeholder="Acil durumda aranacak kişinin adı"
              />

              <Input
                label="Acil Durum Telefonu *"
                type="tel"
                value={formData?.emergencyContactPhone}
                onChange={(e) => handleChange('emergencyContactPhone', e?.target?.value)}
                placeholder="Acil durum telefon numarası"
              />
            </div>
          </div>

          {/* Group 3: Teslimat Adresi */}
          <div className="space-y-5 pt-6 border-t border-border">
            <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
              <Icon name="MapPin" size={20} />
              Teslimat Adresi
            </h3>
            <p className="text-sm text-muted-foreground">Mağazadan sipariş vermek için gereklidir</p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Select
                label="İl *"
                options={CITY_OPTIONS}
                value={formData?.city}
                onChange={(value) => handleChange('city', value)}
                error={errors?.city}
                placeholder="İl seçiniz"
              />

              <Select
                label="İlçe *"
                options={getDistrictOptions(formData?.city)}
                value={formData?.district}
                onChange={(value) => handleChange('district', value)}
                error={errors?.district}
                placeholder={formData?.city ? 'İlçe seçiniz' : 'Önce il seçiniz'}
                disabled={!formData?.city}
              />
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Açık Adres <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={formData?.addressLine}
                  onChange={(e) => handleChange('addressLine', e?.target?.value)}
                  placeholder="Sokak, mahalle, bina no, daire no"
                  rows={3}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>

              <Input
                label="Posta Kodu"
                type="text"
                value={formData?.zipCode}
                onChange={(e) => handleChange('zipCode', e?.target?.value)}
                placeholder="Örn: 34710"
              />
            </div>
          </div>

          {/* Group 3: Sosyal Medya & Ekipman */}
          <div className="space-y-5 pt-6 border-t border-border">
            <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
              <Icon name="Share2" size={20} />
              Sosyal Medya & Ekipman
            </h3>
            
            <Input
              label="Instagram"
              type="text"
              value={formData?.instagramHandle}
              onChange={(e) => handleChange('instagramHandle', e?.target?.value)}
              placeholder="@kullaniciadi"
              description="Instagram kullanıcı adınız"
            />

            <Input
              label="Web Sitesi"
              type="url"
              value={formData?.websiteUrl}
              onChange={(e) => handleChange('websiteUrl', e?.target?.value)}
              placeholder="https://example.com"
              description="Kişisel web siteniz veya blog"
            />

            <div className="space-y-5">
              <label className="block text-sm font-medium text-foreground mb-2">
                Ekipman Detayı
              </label>
              <textarea
                value={formData?.equipmentDetails}
                onChange={(e) => handleChange('equipmentDetails', e?.target?.value)}
                placeholder="Hangi board/kürek kullanıyorsun? (Örn: Red Paddle Co 10'6, Carbon fiber kürek)"
                rows={3}
                className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Kullandığınız SUP board, kürek ve diğer ekipmanlar
              </p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 pt-6 border-t border-border sticky bottom-0 bg-card rounded-b-xl">
            <Button type="submit" variant="default" size="default" fullWidth disabled={uploading}>
              Değişiklikleri Kaydet
            </Button>
            <Button
              type="button"
              variant="outline"
              size="default"
              onClick={onClose}
              fullWidth
              disabled={uploading}
            >
              İptal
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditProfileModal;